


























import vue from 'vue';
import {mapActions, mapGetters} from 'vuex';
import {Root, p50, p100, Tooltip} from '@amcharts/amcharts5';
import {
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  ColumnSeries,
  ValueAxis,
  XYChart,
  XYCursor,
} from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated' ;
import {IXYCursorSettings} from '@amcharts/amcharts5/.internal/charts/xy/XYCursor';
import {IXYChartSettings} from '@amcharts/amcharts5/.internal/charts/xy/XYChart';
import {IAxisRendererXSettings} from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererX';
import {IAxisLabelSettings} from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisLabel';
import {ICategoryAxisSettings} from '@amcharts/amcharts5/.internal/charts/xy/axes/CategoryAxis';
import {AxisRenderer} from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRenderer';
import {ITooltipSettings} from '@amcharts/amcharts5/.internal/core/render/Tooltip';
import {IValueAxisSettings} from '@amcharts/amcharts5/.internal/charts/xy/axes/ValueAxis';
import {IAxisRendererYSettings} from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererY';
import {IColumnSeriesSettings} from '@amcharts/amcharts5/.internal/charts/xy/series/ColumnSeries';
import {StatsItem} from '#/Stats';
import {_axios} from '@/plugins/axios';
import {AxiosResponse} from 'axios';
import dayjs from 'dayjs';

enum Chips {
  'ADMIN_MESSAGE_NUMBER',
  'MESSAGE_NUMBER',
  'PUBLISHED_ADVERTS_NUMBER',
  'USER_MESSAGE_NUMBER',
  'USER_NUMBER'
}

interface Data {
  date: string,
  value: number,
}

interface ICategoryAxisSettingsNullable<R extends AxisRenderer> extends Omit<ICategoryAxisSettings<R>, 'renderer'> {
  renderer?: AxisRendererX;
}

interface IValueAxisSettingsNullable<R extends AxisRenderer> extends Omit<IValueAxisSettings<R>, 'renderer'> {
  renderer?: AxisRendererY;
}

interface IColumnSeriesSettingsNullable extends Omit<IColumnSeriesSettings, 'xAxis' | 'yAxis'> {
  renderer?: AxisRendererY;
}

interface ComponentData {
  chart: {
    chart: {
      content: XYChart | null;
      settings: IXYChartSettings;
    };
    cursor: {
      content: XYCursor | null;
      settings: IXYCursorSettings;
    };
    root: Root | null;
    series: {
      content: ColumnSeries | null;
      settings: IColumnSeriesSettingsNullable;
      tooltipSettings: ITooltipSettings;
    };
    xAxis: {
      content: CategoryAxis<AxisRendererX> | null;
      settings: ICategoryAxisSettingsNullable<AxisRendererX>;
      tooltipSettings: ITooltipSettings;
    };
    yAxis: {
      axisSettings: IAxisRendererYSettings;
      content: ValueAxis<AxisRendererY> | null;
      settings: IValueAxisSettingsNullable<AxisRendererY>;
    };
    xRenderer: {
      content: AxisRendererX | null;
      labelsSettings: IAxisLabelSettings;
      settings: IAxisRendererXSettings;
    };
  };
  noData: boolean;
  statSelected: Chips | null;
}

export default vue.extend({
  name: 'Stats',

  data: (): ComponentData => ({
    chart: {
      chart: {
        content: null,
        settings: {
          wheelX: 'none',
          wheelY: 'none',
        },
      },
      cursor: {
        content: null,
        settings: {},
      },
      root: null,
      series: {
        content: null,
        settings: {
          name: 'Series 1',
          valueYField: 'value',
          sequencedInterpolation: true,
          categoryXField: 'date',
        },
        tooltipSettings: {
          labelText: '{valueY}',
        },
      },
      xAxis: {
        content: null,
        settings: {
          categoryField: 'date',
        },
        tooltipSettings: {},
      },
      yAxis: {
        axisSettings: {},
        content: null,
        settings: {
          min: 0,
          maxDeviation: 0.3,
        },
      },
      xRenderer: {
        content: null,
        labelsSettings: {
          rotation: -90,
          centerY: p50,
          centerX: p100,
          paddingRight: 15,
        },
        settings: {minGridDistance: 30},
      },
    },
    noData: true,
    statSelected: null,
  }),

  computed: {
    ...mapGetters([
      'stats',
    ]),
  },

  watch: {
    statSelected: function () {
      if (this.statSelected === undefined) {
        this.noData = true;
        return;
      }

      switch (this.statSelected) {
        case Chips.ADMIN_MESSAGE_NUMBER:
          this.updateChartData(this.stats.adminMessageNumber);
          break;
        case Chips.MESSAGE_NUMBER:
          this.updateChartData(this.stats.messageNumber);
          break;
        case Chips.PUBLISHED_ADVERTS_NUMBER:
          this.updateChartData(this.stats.publishedAdvertsNumber);
          break;
        case Chips.USER_MESSAGE_NUMBER:
          this.updateChartData(this.stats.userMessageNumber);
          break;
        case Chips.USER_NUMBER:
          this.updateChartData(this.stats.userNumber);
          break;
      }
    },
  },

  created() {
    this.updatePageTitle(this.$t('template.sideMenu.stats'));
    this.loadStats();
  },

  mounted() {
    this.noData = true;

    /* Chart code */
    // Create root element
    // https://www.amcharts.com/docs/v5/getting-started/#Root_element
    this.chart.root = Root.new('chartDiv');

    // Set themes
    // https://www.amcharts.com/docs/v5/concepts/themes/
    this.chart.root.setThemes([
      am5themes_Animated.new(this.chart.root),
    ]);

    // Create chart
    // https://www.amcharts.com/docs/v5/charts/xy-chart/
    this.chart.chart.content = this.chart.root.container.children.push(
        XYChart.new(this.chart.root, this.chart.chart.settings),
    );

    // Add cursor
    // https://www.amcharts.com/docs/v5/charts/xy-chart/cursor/
    this.chart.cursor.content = this.chart.chart.content.set(
        'cursor',
        XYCursor.new(this.chart.root, this.chart.cursor.settings),
    );
    this.chart.cursor.content.lineY.set('visible', false);

    // Create axes
    // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
    this.chart.xRenderer.content = AxisRendererX.new(this.chart.root, {minGridDistance: 30});
    this.chart.xRenderer.content.labels.template.setAll(this.chart.xRenderer.labelsSettings);

    // Add the renderer and the tooltip in the settings already created in the data
    this.chart.xAxis.content = this.chart.chart.content.xAxes.push(
        CategoryAxis.new(
            this.chart.root,
            {
              ...this.chart.xAxis.settings,
              renderer: this.chart.xRenderer.content,
              tooltip: Tooltip.new(this.chart.root, this.chart.xAxis.tooltipSettings),
            } as ICategoryAxisSettings<AxisRendererX>,
        ),
    ) as CategoryAxis<AxisRendererX>;


    this.chart.yAxis.content = this.chart.chart.content.yAxes.push(
        ValueAxis.new(
            this.chart.root,
            {
              ...this.chart.yAxis.settings,
              renderer: AxisRendererY.new(this.chart.root, this.chart.yAxis.axisSettings),
            } as IValueAxisSettings<AxisRendererY>,
        ),
    ) as ValueAxis<AxisRendererY>;

    // Create series
    // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
    this.chart.series.content = this.chart.chart.content.series.push(
        ColumnSeries.new(
            this.chart.root,
            {
              ...this.chart.series.settings,
              tooltip: Tooltip.new(this.chart.root, this.chart.series.tooltipSettings),
              xAxis: this.chart.xAxis.content,
              yAxis: this.chart.yAxis.content,
            } as IColumnSeriesSettings,
        ),
    );

    this.chart.series.content.columns.template.setAll({cornerRadiusTL: 5, cornerRadiusTR: 5});
    this.chart.series.content.columns.template.adapters.add('fill', (fill, target) => {
      // @ts-ignore
      return this.chart.chart.content.get('colors')?.getIndex(this.chart.series.content.columns.indexOf(target));
    });

    this.chart.series.content.columns.template.adapters.add('stroke', (stroke, target) => {
      // @ts-ignore
      return this.chart.chart.content.get('colors')?.getIndex(this.chart.series.content.columns.indexOf(target));
    });


    // Set data
    this.chart.xAxis.content.data.setAll([]);
    this.chart.series.content.data.setAll([]);


    // Make stuff animate on load
    // https://www.amcharts.com/docs/v5/concepts/animations/
    this.chart.series.content.appear(1000);
    this.chart.chart.content.appear(1000, 100);
  },

  methods: {
    ...mapActions([
      'loadStats',
      'updatePageTitle',
    ]),
    onCreateCSV(): void {
      _axios.get('/stats/?export=csv')
            .then((response: AxiosResponse<string>) => {
              let link = document.createElement('a');
              link.setAttribute('href', encodeURI('data:text/csv;charset=utf-8,' + response.data));
              link.setAttribute('download', `stats_feu_${(dayjs()).format('L_LT')}.csv`);
              document.body.appendChild(link); // Required for FF
              link.click();
              document.body.removeChild(link);
            });
    },
    updateChartData(stats: StatsItem[]): void {
      if (stats.length === 0) {
        this.noData = true;
        return;
      }

      const data: Data[] = [];
      stats
          .sort((statA, statB) => {
            if (statA.month.isSameOrAfter(statB.month)) return 1;
            if (statB.month.isSameOrAfter(statA.month)) return -1;
            return 0;
          })
          .forEach((stat) => {
            data.push({
              date: stat.month.format('MM/YYYY'),
              value: stat.num,
            });
          });

      (this.chart.xAxis.content as CategoryAxis<AxisRendererX>).data.setAll(data);
      (this.chart.series.content as ColumnSeries).data.setAll(data);
      this.noData = false;
    },
  },
});
